import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { ChartBar, Download, CheckCircle, Clock, Warning, Hourglass, ArrowsClockwise } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

const STATUS_META = {
  planning:    { l: 'Planning',    bg: '#e0e7ff', fg: '#3730a3' },
  in_progress: { l: 'In Progress', bg: '#dbeafe', fg: '#1d4ed8' },
  on_hold:     { l: 'On Hold',     bg: '#fef3c7', fg: '#92400e' },
  completed:   { l: 'Completed',   bg: '#dcfce7', fg: '#15803d' },
  cancelled:   { l: 'Cancelled',   bg: '#fee2e2', fg: '#b91c1c' },
};

const progressColor = (pct) => {
  if (pct >= 80) return '#15803d';
  if (pct >= 50) return '#1d4ed8';
  if (pct >= 20) return '#a16207';
  return '#b91c1c';
};

export default function CoordinatorDashboardPage() {
  const [from, setFrom] = useState(daysAgoISO(30));
  const [to, setTo] = useState(todayISO());
  const [data, setData] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/coordinator/project-dashboard`, {
        params: { date_from: from, date_to: to },
        withCredentials: true,
      });
      setData(r.data || { rows: [], totals: {} });
    } catch (e) {
      toast.error('Failed to load: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data.rows || [];
    return (data.rows || []).filter((r) =>
      (r.project_name || '').toLowerCase().includes(q)
      || (r.client_name || '').toLowerCase().includes(q)
    );
  }, [data.rows, search]);

  const exportCsv = () => {
    const header = ['Project', 'Client', 'Status', 'Total Tasks', 'Completed', 'In Progress', 'Pending', 'Blocked', 'Client Waiting', 'Progress %'];
    const lines = [header.join(',')];
    rows.forEach((r) => {
      const cells = [
        `"${(r.project_name || '').replace(/"/g, '""')}"`,
        `"${(r.client_name || '').replace(/"/g, '""')}"`,
        r.status || '',
        r.total_tasks, r.completed, r.in_progress, r.pending, r.blocked, r.client_waiting,
        `${r.progress_pct}%`,
      ];
      lines.push(cells.join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `project-dashboard-${from}_to_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const t = data.totals || {};

  return (
    <div className="space-y-4" data-testid="coordinator-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{ color: '#1a2e4a' }}>
            <ChartBar size={22} weight="duotone" /> Coordinator Dashboard
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            One row per project — Total tasks with completed, in-progress, pending, blocked, and client-waiting counts. Google-sheets-style rollup for the daily coordinator standup.
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div><Label className="text-[10px]">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-36" data-testid="cd-from" /></div>
          <div><Label className="text-[10px]">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-36" data-testid="cd-to" /></div>
          <Button size="sm" variant="outline" onClick={load} data-testid="cd-refresh"><ArrowsClockwise size={14} className="mr-1" />Refresh</Button>
          <Button size="sm" variant="outline" onClick={exportCsv} data-testid="cd-export"><Download size={14} className="mr-1" />Export CSV</Button>
        </div>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
        <Card className="p-3 bg-slate-50"><div className="text-[10px] uppercase font-semibold tracking-wide text-gray-500">Total Tasks</div><div className="text-2xl font-black mt-0.5" data-testid="cd-total">{t.total || 0}</div></Card>
        <Card className="p-3 bg-emerald-50"><div className="text-[10px] uppercase font-semibold tracking-wide text-emerald-700 flex items-center gap-1"><CheckCircle size={11} weight="fill" />Completed</div><div className="text-2xl font-black mt-0.5 text-emerald-700" data-testid="cd-completed">{t.completed || 0}</div></Card>
        <Card className="p-3 bg-blue-50"><div className="text-[10px] uppercase font-semibold tracking-wide text-blue-700 flex items-center gap-1"><Clock size={11} weight="fill" />In Progress</div><div className="text-2xl font-black mt-0.5 text-blue-700" data-testid="cd-inprogress">{t.in_progress || 0}</div></Card>
        <Card className="p-3 bg-purple-50"><div className="text-[10px] uppercase font-semibold tracking-wide text-purple-700 flex items-center gap-1"><Hourglass size={11} weight="fill" />Pending</div><div className="text-2xl font-black mt-0.5 text-purple-700" data-testid="cd-pending">{t.pending || 0}</div></Card>
        <Card className="p-3 bg-rose-50"><div className="text-[10px] uppercase font-semibold tracking-wide text-rose-700 flex items-center gap-1"><Warning size={11} weight="fill" />Blocked</div><div className="text-2xl font-black mt-0.5 text-rose-700" data-testid="cd-blocked">{t.blocked || 0}</div></Card>
        <Card className="p-3 bg-amber-50"><div className="text-[10px] uppercase font-semibold tracking-wide text-amber-800">Client Waiting</div><div className="text-2xl font-black mt-0.5 text-amber-800" data-testid="cd-waiting">{t.client_waiting || 0}</div></Card>
      </div>

      {/* Search */}
      <div className="flex items-center gap-2">
        <Input
          placeholder="Search project or client name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-md"
          data-testid="cd-search"
        />
        {rows.length > 0 && <span className="text-xs text-gray-500">{rows.length} project{rows.length === 1 ? '' : 's'} shown · overall progress <b style={{ color: progressColor(t.progress_pct || 0) }}>{t.progress_pct || 0}%</b></span>}
      </div>

      {/* Table */}
      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow style={{ background: '#f8fafc' }}>
              <TableHead className="text-[10px] uppercase">Project</TableHead>
              <TableHead className="text-[10px] uppercase">Client</TableHead>
              <TableHead className="text-[10px] uppercase">Status</TableHead>
              <TableHead className="text-[10px] uppercase text-right">Total</TableHead>
              <TableHead className="text-[10px] uppercase text-right text-emerald-700">Completed</TableHead>
              <TableHead className="text-[10px] uppercase text-right text-blue-700">In Progress</TableHead>
              <TableHead className="text-[10px] uppercase text-right text-purple-700">Pending</TableHead>
              <TableHead className="text-[10px] uppercase text-right text-rose-700">Blocked</TableHead>
              <TableHead className="text-[10px] uppercase text-right text-amber-800">Client Waiting</TableHead>
              <TableHead className="text-[10px] uppercase text-right w-32">Progress %</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} className="text-center py-10 text-gray-400">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-10 text-gray-500">No projects in this range.</TableCell></TableRow>
            ) : rows.map((r) => {
              const sm = STATUS_META[r.status] || { l: (r.status || '—').replace('_', ' '), bg: '#f3f4f6', fg: '#374151' };
              const pctColor = progressColor(r.progress_pct);
              return (
                <TableRow key={r.project_id} data-testid={`cd-row-${r.project_id}`}>
                  <TableCell className="font-semibold text-sm py-2">{r.project_name || '(unnamed)'}</TableCell>
                  <TableCell className="text-xs text-gray-600 py-2">{r.client_name || '—'}</TableCell>
                  <TableCell className="py-2">
                    <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded" style={{ background: sm.bg, color: sm.fg }}>{sm.l}</span>
                  </TableCell>
                  <TableCell className="text-right font-bold tabular-nums py-2">{r.total_tasks}</TableCell>
                  <TableCell className="text-right tabular-nums py-2 text-emerald-700">{r.completed || <span className="text-gray-300">—</span>}</TableCell>
                  <TableCell className="text-right tabular-nums py-2 text-blue-700">{r.in_progress || <span className="text-gray-300">—</span>}</TableCell>
                  <TableCell className="text-right tabular-nums py-2 text-purple-700">{r.pending || <span className="text-gray-300">—</span>}</TableCell>
                  <TableCell className="text-right tabular-nums py-2 text-rose-700">{r.blocked || <span className="text-gray-300">—</span>}</TableCell>
                  <TableCell className="text-right tabular-nums py-2 text-amber-800">{r.client_waiting || <span className="text-gray-300">—</span>}</TableCell>
                  <TableCell className="py-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 rounded bg-gray-100 overflow-hidden">
                        <div className="h-full rounded" style={{ width: `${r.progress_pct}%`, background: pctColor }} />
                      </div>
                      <span className="text-xs font-bold tabular-nums w-10 text-right" style={{ color: pctColor }}>{r.progress_pct}%</span>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
